'use client';
import { jsx } from 'react/jsx-runtime';
import cl from 'clsx/lite';
import { createContext, forwardRef, useId, useState } from 'react';

const ToggleGroupContext = createContext({});
/**
 * Display a group of buttons that can be toggled between.
 *
 * @example
 * <ToggleGroup data-toggle-group="Label" onChange={(value) => console.log(value)}>
 *   <ToggleGroup.Item value='1'>Toggle 1</ToggleGroup.Item>
 *   <ToggleGroup.Item value='2'>Toggle 2</ToggleGroup.Item>
 *   <ToggleGroup.Item value='3'>Toggle 3</ToggleGroup.Item>
 * </ToggleGroup>
 */
const ToggleGroup = forwardRef(function ToggleGroup({ children, className, defaultValue, name, onChange, value, variant = 'primary', ...rest }, ref) {
    const nameId = useId();
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    let onValueChange = onChange;
    if (!isControlled) {
        onValueChange = (newValue) => {
            setUncontrolledValue(newValue);
            onChange?.(newValue);
        };
        value = uncontrolledValue;
    }
    return (jsx(ToggleGroupContext.Provider, { value: {
            defaultValue,
            name: name ?? `togglegroup-name-${nameId}`,
            onChange: onValueChange,
            value,
            variant,
        }, children: jsx("fieldset", { className: cl('ds-toggle-group', className), "data-toggle-group": '' // Default to empty string to ensure attribute is present
            , "data-variant": variant, suppressHydrationWarning // Since @digdir/designsystemet-web adds attributes
            : true, ref: ref, ...rest, children: children }) }));
});

export { ToggleGroup, ToggleGroupContext };
